import { AccountInfo, BrowserCacheLocation, PublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { environment } from '@envs/environment'
import { jwtDecode } from 'jwt-decode';

const { msalClientId, msalTenantId, msalRedirectUri, msalScope, adminGroupId } = environment

// Convertir les scopes en tableau
const scopes = msalScope ? msalScope.split(',').map(s => s.trim()) : [];

/**
 * Détecte si l'application tourne dans une iframe (Teams, etc.)
 */
const isInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Si on ne peut pas accéder à window.top, c'est qu'on est dans une iframe cross-origin
    return true;
  }
};

export const msalConfig = {
  auth: {
    clientId: msalClientId,
    authority: `https://login.microsoftonline.com/${msalTenantId}`,
    redirectUri: msalRedirectUri,
    navigateToLoginRequestUrl: !isInIframe(), // Désactiver dans les iframes
    postLogoutRedirectUri: msalRedirectUri,
  },
  cache: {
    cacheLocation: BrowserCacheLocation.LocalStorage,
    storeAuthStateInCookie: false,
  },
  system: {
    allowNativeBroker: false,
    allowRedirectInIframe: false, // Ne JAMAIS autoriser les redirects dans les iframes
    loggerOptions: {
      logLevel: 0,
      piiLoggingEnabled: false,
    },
  },
}

export const loginRequest = {
  scopes: scopes,
}

// Création de l'instance MSAL
const msalInstance = new PublicClientApplication(msalConfig);

// Promise d'initialisation pour s'assurer que MSAL est prêt
let msalInitialized = false;
let initializePromise: Promise<void> | null = null;

const ensureMsalInitialized = async (): Promise<void> => {
  if (msalInitialized) return;
  
  if (!initializePromise) {
    initializePromise = msalInstance.initialize().then(() => {
      msalInitialized = true;
      // Gérer la réponse de redirection si présente
      return msalInstance.handleRedirectPromise().then((response) => {
        if (response?.accessToken) {
          console.log('Token obtenu après redirection');
        }
      });
    });
  }
  
  await initializePromise;
};

// Initialiser MSAL au chargement
ensureMsalInitialized().catch(error => 
  console.error("Erreur lors de l'initialisation de MSAL:", error)
);

/**
 * Vérifie si un token est valide (non expiré avec une marge de sécurité)
 */
const isTokenValid = (token: string, marginSeconds: number = 300): boolean => {
  try {
    const decoded: { exp: number } = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);
    // Considérer le token invalide 5 minutes avant son expiration réelle
    return decoded.exp > (currentTime + marginSeconds);
  } catch {
    return false;
  }
};

/**
 * Obtient un token frais depuis MSAL (toujours demander à MSAL, ne pas utiliser le cache localStorage)
 */
export const getToken = async (account: AccountInfo | null): Promise<string | null> => {
  try {
    await ensureMsalInitialized();
    
    // 1. Obtenir le compte actif
    const accounts = msalInstance.getAllAccounts();
    const activeAccount = account || accounts[0] || null;

    if (!activeAccount) {
      console.log('Pas de compte actif, connexion requise');
      return await performLogin();
    }

    // 2. Toujours demander un token à MSAL (il gère son propre cache)
    const silentRequest: SilentRequest = {
      account: activeAccount,
      scopes: scopes,
      forceRefresh: false, // MSAL décidera si un refresh est nécessaire
    };

    try {
      const response = await msalInstance.acquireTokenSilent(silentRequest);
      
      if (response.accessToken && isTokenValid(response.accessToken)) {
        return response.accessToken;
      }
      
      // Token obtenu mais proche de l'expiration, forcer le refresh
      console.log('Token proche de l\'expiration, renouvellement...');
      const refreshResponse = await msalInstance.acquireTokenSilent({
        ...silentRequest,
        forceRefresh: true,
      });
      
      return refreshResponse.accessToken;
      
    } catch (silentError: any) {
      console.log('acquireTokenSilent a échoué:', silentError.errorCode || silentError.message);
      
      // Si interaction requise, utiliser popup ou redirect
      if (
        silentError.errorCode === 'interaction_required' ||
        silentError.errorCode === 'login_required' ||
        silentError.errorCode === 'consent_required' ||
        silentError.errorCode === 'no_tokens_found' ||
        silentError.errorCode === 'token_refresh_required'
      ) {
        return await performInteractiveLogin(activeAccount);
      }
      
      throw silentError;
    }
    
  } catch (error: any) {
    console.error("Erreur lors de l'acquisition du token:", error);
    
    // En cas d'erreur grave, nettoyer et reconnecter
    if (error.errorCode === 'invalid_grant' || error.message?.includes('AADSTS')) {
      console.log('Session invalide, nettoyage et reconnexion...');
      clearAllAuthStorage();
      return await performLogin();
    }
    
    return null;
  }
};

/**
 * Effectue une connexion interactive (popup uniquement dans iframe, sinon popup avec fallback redirect)
 */
const performLogin = async (): Promise<string | null> => {
  try {
    const response = await msalInstance.loginPopup(loginRequest);
    return response.accessToken || null;
  } catch (popupError: any) {
    // Dans une iframe (Teams), on ne peut PAS utiliser redirect
    if (isInIframe()) {
      console.error('Authentification par popup a échoué dans Teams. Vérifiez que les popups sont autorisées.');
      throw new Error('Popup authentication failed in iframe context. Please allow popups for this site.');
    }
    
    console.log('Popup bloquée ou erreur, utilisation de redirect');
    await msalInstance.loginRedirect(loginRequest);
    return null;
  }
};

/**
 * Effectue une acquisition de token interactive
 */
const performInteractiveLogin = async (account: AccountInfo): Promise<string | null> => {
  try {
    const response = await msalInstance.acquireTokenPopup({
      ...loginRequest,
      account: account,
    });
    return response.accessToken;
  } catch (popupError: any) {
    // Dans une iframe (Teams), on ne peut PAS utiliser redirect
    if (isInIframe()) {
      console.error('Authentification par popup a échoué dans Teams. Vérifiez que les popups sont autorisées.');
      throw new Error('Popup authentication failed in iframe context. Please allow popups for this site.');
    }
    
    console.log('Popup bloquée, utilisation de redirect');
    await msalInstance.acquireTokenRedirect({
      ...loginRequest,
      account: account,
    });
    return null;
  }
};

/**
 * Nettoie tout le stockage d'authentification
 */
const clearAllAuthStorage = () => {
  // Supprimer notre token custom
  localStorage.removeItem("token");
  
  // Supprimer toutes les clés MSAL du localStorage
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (key.startsWith('msal.') || key.includes(msalClientId))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
  
  // Supprimer du sessionStorage aussi
  const sessionKeysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.startsWith('msal.') || key.includes(msalClientId))) {
      sessionKeysToRemove.push(key);
    }
  }
  sessionKeysToRemove.forEach(key => sessionStorage.removeItem(key));
};

export const isAdmin = async (account: AccountInfo): Promise<boolean> => {
  if (!account) {
    return false;
  }

  try {
    const token = await getToken(account);

    if (!token) {
      return false;
    }

    const decodedToken: { groups?: string[] } = jwtDecode(token);
    const tokenGroups = decodedToken?.groups;

    if (!tokenGroups || tokenGroups.length === 0) {
      console.warn('Aucun groupe trouvé dans les claims du token');
      return true; // Par défaut admin si pas de groupes configurés
    }

    if (adminGroupId) {
      return tokenGroups.includes(adminGroupId);
    }

    return true;
  } catch (error) {
    console.error('Erreur lors de la vérification admin:', error);
    return false;
  }
};

/**
 * Force le renouvellement du token (utile après une erreur 401)
 */
export const forceTokenRefresh = async (account: AccountInfo | null): Promise<string | null> => {
  try {
    await ensureMsalInitialized();
    
    const accounts = msalInstance.getAllAccounts();
    const activeAccount = account || accounts[0] || null;

    if (!activeAccount) {
      return await performLogin();
    }

    const response = await msalInstance.acquireTokenSilent({
      account: activeAccount,
      scopes: scopes,
      forceRefresh: true,
    });

    return response.accessToken;
  } catch (error: any) {
    console.error('Erreur lors du refresh forcé:', error);
    
    if (error.errorCode === 'interaction_required' || error.errorCode === 'login_required') {
      return await performInteractiveLogin(account as AccountInfo);
    }
    
    return null;
  }
};

export const logout = async () => {
  clearAllAuthStorage();
  
  // Dans une iframe (Teams), utiliser logoutPopup au lieu de logoutRedirect
  if (isInIframe()) {
    try {
      await msalInstance.logoutPopup({
        postLogoutRedirectUri: msalRedirectUri,
        mainWindowRedirectUri: msalRedirectUri,
      });
    } catch (error) {
      console.error('Logout popup failed:', error);
      // En cas d'échec, juste nettoyer le storage local
    }
  } else {
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: msalRedirectUri,
    });
  }
};